// Display text names the live-streaming unit "ULLFU" (Ultra Low Latency Forwarding Unit). Visible
// text only: code names (ULLFU*, sfu*), API paths and console logs still say ULLFU.
/**
 * Packet-chain analysis — turns a monitor report into "where did the packets go".
 *
 * The hop percentages on their own are misleading, and that is the whole reason
 * this exists. A run can report Pipe→ULLFU 97.8%, ULLFU R→S 100.0%, ULLFU→FE 99.9%
 * and look healthy, while a 25fps/2048kbps camera arrives in the browser at
 * 16fps/1083kbps. Nothing is wrong with those percentages — they are simply all
 * measured from `rtp_sent` onward, which is AFTER the two places the stream
 * actually degrades:
 *
 *   1. Camera → jitterbuffer. If drop-on-latency is on, rtspsrc discards
 *      buffered RTP without incrementing num-lost, num-late or anything else.
 *      The packets are gone before the first counter on the path sees them, so
 *      every hop downstream still balances perfectly.
 *   2. Inside the pipeline. A frame missing half its packets is still ONE frame
 *      at depay, so frame counts stay flat while bytes collapse. Only the
 *      per-stage byte ledger shows it.
 *
 * So the verdict below is driven by BYTE survival and the jitterbuffer state,
 * not by the hop percentages.
 *
 * TRANSCODING SPLITS THE LEDGER IN TWO. An H265 camera is decoded and re-encoded
 * to H264, and the encoder reduces bytes ON PURPOSE — 1300 kbps in, 650 out is
 * the pipeline working. Comparing anything downstream of the encoder against
 * what arrived from the camera therefore reports correct operation as ~50% loss.
 * Stages before the encoder are measured against the camera; stages after it
 * against the encoder's own output; the encoder itself gets a ratio, never a
 * survival figure. `report.pipeline.transcoding` is what decides which.
 */

export interface ChainStage {
  key: string;
  label: string;
  /** Primary count for this stage (packets, or frames where noted). */
  value: number | null;
  unit: 'pkts' | 'frames' | 'NALs' | 'kbps';
  /** Percentage of the stage before it, when both are known. */
  survivalPct: number | null;
  /** Set when this stage is where the stream degrades. */
  isLoss: boolean;
  note?: string;
}

export interface ChainDiagnosis {
  stages: ChainStage[];
  /** One-line plain answer to "where are my packets going". */
  verdict: string;
  severity: 'ok' | 'warn' | 'bad';
  /** Specific, actionable follow-ups. */
  hints: string[];
}

const pct = (num: number, den: number): number | null =>
  den > 0 ? Math.round((num / den) * 1000) / 10 : null;

/** Packet-count hops (pipeline→ULLFU→browser). These are exact counts of the same
 *  objects at both ends, so a few percent really is loss. */
const LOSS_THRESHOLD = 95;

/** Byte survival BETWEEN PIPELINE STAGES needs a much looser bar. The stages are
 *  separated by queues — `srcq` alone holds up to 400 buffers, and the
 *  jitterbuffer another second — so over a 60s window a stage can read anywhere
 *  from ~85% to ~110% of the one before it purely from what is in flight at the
 *  two sampling instants. Measured across all 32 cameras on a live node in one
 *  60s window: depay 84.6–109.9%, parse 84.9–109.3%. A 95% bar would call most
 *  of a healthy fleet broken. */
const BYTE_LOSS_THRESHOLD = 85;

/** `rtph264pay mtu=1200` in the live pipeline — a hard per-packet byte ceiling. */
const RTP_MTU = 1200;

/** Below this share of input frames reaching the encoder's output, the leaky
 *  queue in front of it is dropping — the encoder cannot keep up. */
const ENCODER_KEEP_THRESHOLD = 90;
// The node derives leaky-queue drops as (in - out - currently_queued) from two
// pad probes plus a level read, which are not sampled atomically. A buffer that
// has been dequeued but not yet pushed past the src probe reads as dropped, so
// the count carries a standing bias of about one buffer per queue — it does not
// accumulate, but it is never exactly zero on a healthy pipeline. Anything at or
// below this is that artefact, not a discard worth naming.
const TRANSCODE_DROP_FLOOR = 3;

export function diagnoseChain(report: any): ChainDiagnosis | null {
  if (!report) return null;

  const pipeline = report.pipeline || {};
  const sfu = report.sfu || {};
  const fe = report.frontend || {};
  const bytes = pipeline.bytes || {};
  const surv = pipeline.stage_survival || {};
  const jb = pipeline.jitterbuffer || {};

  const rtspIn = bytes.rtsp_in ?? null;
  const depayPct = surv.depay ?? null;
  const rtpOutPct = surv.rtp_out ?? null;
  const transcoding = pipeline.transcoding === true;

  // FRAME survival across the encoder — deliberately not byte survival, which
  // is meaningless here. The queue feeding the encoder is
  // `leaky=downstream max-size-buffers=12`, so when the encoder cannot keep up
  // it silently discards frames: no counter, no error, pipeline still "running".
  // Comparing frames in to frames out is the only way that shows.
  // Denominator is the DECODER's output, never the parser's. The parser counts
  // access units of the SOURCE; on a transcode the decoder legitimately delivers
  // a different number, and dividing by the parser reported a pipeline keeping
  // 71% of its frames as keeping 34%.
  const decodeFrames = pipeline.decode_frames ?? null;
  // Below the floor the number is sampling noise, so report nothing rather than
  // "1 frame dropped" on a pipeline that is behaving.
  const rawDrops = Math.round(pipeline.transcode_drops ?? 0);
  const transcodeDrops = rawDrops > TRANSCODE_DROP_FLOOR ? rawDrops : 0;
  const encKeptPct = transcoding && decodeFrames && pipeline.encode_frames != null
    ? Math.round(pipeline.encode_frames / decodeFrames * 1000) / 10
    : null;

  const decoderStage: ChainStage[] = transcoding && decodeFrames
    ? [{
      key: 'decode',
      label: 'Decoder out',
      value: decodeFrames,
      unit: 'frames',
      survivalPct: null,
      isLoss: false,
      note: 'frames offered to the encoder',
    }]
    : [];

  const encoderStage: ChainStage[] = transcoding
    ? [{
      key: 'encode',
      label: 'Encoder',
      value: pipeline.encode_frames ?? null,
      unit: 'frames',
      // Frames kept, NOT byte survival: the encoder is supposed to shed bytes,
      // it is never supposed to shed frames.
      survivalPct: encKeptPct,
      isLoss: encKeptPct != null && encKeptPct < ENCODER_KEEP_THRESHOLD,
      note: [
        transcodeDrops
          ? `${transcodeDrops} frame${transcodeDrops === 1 ? '' : 's'} dropped by the leaky queue`
          : null,
        pipeline.codec_source && pipeline.codec_output
          ? `${pipeline.codec_source}→${pipeline.codec_output}`
          : null,
        bytes.rtsp_in_kbps != null && bytes.encode_kbps != null
          ? `${bytes.rtsp_in_kbps} → ${bytes.encode_kbps} kbps by design`
          : null,
      ].filter(Boolean).join(' · ') || 'transcoding — byte reduction is intended',
    }]
    : [];

  const stages: ChainStage[] = [
    {
      key: 'camera',
      label: 'Camera → jitterbuffer',
      value: jb.available ? jb.num_pushed ?? null : null,
      unit: 'pkts',
      survivalPct: null,
      isLoss: false,
      note: jb.available
        ? `lost ${jb.num_lost ?? 0} · late ${jb.num_late ?? 0} · drop-on-latency ${jb.drop_on_latency ? 'ON' : 'off'}`
        : 'jitterbuffer counters unavailable',
    },
    {
      key: 'rtsp_in',
      label: 'Pipeline in (RTSP)',
      value: pipeline.rtsp_packets ?? null,
      unit: 'pkts',
      survivalPct: null,
      isLoss: false,
      note: bytes.rtsp_in_kbps != null ? `${bytes.rtsp_in_kbps} kbps` : undefined,
    },
    {
      key: 'depay',
      label: 'Depayloader',
      // rtph264depay emits one buffer per NAL unit, not per frame: a keyframe
      // arrives as SPS + PPS + IDR. Counting these as frames makes the parser
      // below — which regroups them into access units — look like it is losing
      // most of them, which is how a healthy 10fps camera read as 1402 → 577.
      value: pipeline.rtsp_frames ?? null,
      unit: 'NALs',
      survivalPct: depayPct,
      isLoss: depayPct != null && depayPct < BYTE_LOSS_THRESHOLD,
      note: 'byte survival vs RTSP in',
    },
    {
      key: 'parse',
      label: 'Parser',
      value: pipeline.pipeline_frames ?? null,
      unit: 'frames',
      survivalPct: surv.parse ?? null,
      // Compared against depay, never against a fixed threshold: both sit on
      // the camera side of any encoder, so they should track each other.
      isLoss: surv.parse != null && depayPct != null && surv.parse < depayPct - 8,
      note: 'access units — NALs regrouped into frames',
    },
    ...decoderStage,
    ...encoderStage,
    {
      key: 'rtp_out',
      label: 'Pipeline out → ULLFU',
      value: pipeline.rtp_sent ?? null,
      unit: 'pkts',
      survivalPct: rtpOutPct,
      // Only judged when the denominator is trustworthy. On a transcoding
      // pipeline that means the encoder's own byte counter exists; without it
      // this percentage is measured against the camera and will read ~50% on a
      // perfectly healthy stream.
      isLoss:
        rtpOutPct != null && rtpOutPct < BYTE_LOSS_THRESHOLD &&
        (!transcoding || (bytes.encode ?? 0) > 0),
      note: transcoding ? 'byte survival vs encoder output' : undefined,
    },
    {
      key: 'udpsink',
      label: 'udpsink → ULLFU socket',
      value: pipeline.udp_packets ?? null,
      unit: 'pkts',
      survivalPct: surv.udpsink ?? null,
      // The last stage inside the pipeline. It exists to make the payloader
      // checkable: rtph264pay and udpsink should agree packet-for-packet, and
      // whichever of them the ULLFU disagrees with is the one to trust less.
      isLoss:
        pipeline.rtp_sent > 0 && pipeline.udp_packets > 0 &&
        pipeline.udp_packets < pipeline.rtp_sent * 0.95,
    },
    {
      key: 'sfu_recv',
      label: 'ULLFU received',
      value: sfu.packets_received ?? null,
      unit: 'pkts',
      survivalPct: pct(sfu.packets_received ?? 0, pipeline.udp_packets || pipeline.rtp_sent || 0),
      isLoss:
        (pipeline.udp_packets || pipeline.rtp_sent) > 0 &&
        (pct(sfu.packets_received ?? 0, pipeline.udp_packets || pipeline.rtp_sent) ?? 100) < LOSS_THRESHOLD,
    },
    {
      key: 'sfu_sent',
      label: 'ULLFU forwarded',
      value: sfu.packets_sent ?? null,
      unit: 'pkts',
      survivalPct: pct(sfu.packets_sent ?? 0, sfu.packets_received ?? 0),
      isLoss: false,
    },
    {
      key: 'fe_recv',
      label: 'Browser received',
      value: fe.packets_received ?? null,
      unit: 'pkts',
      survivalPct: pct(fe.packets_received ?? 0, sfu.packets_sent ?? 0),
      isLoss:
        sfu.packets_sent > 0 &&
        (pct(fe.packets_received ?? 0, sfu.packets_sent) ?? 100) < LOSS_THRESHOLD,
    },
    {
      key: 'fe_decoded',
      label: 'Browser decoded',
      value: fe.frames_decoded ?? null,
      unit: 'frames',
      survivalPct: null,
      isLoss: false,
      note: fe.frames_dropped ? `${fe.frames_dropped} dropped` : undefined,
    },
  ];

  const hints: string[] = [];
  let verdict = 'No significant loss found on any hop.';
  let severity: ChainDiagnosis['severity'] = 'ok';

  // ── Internal consistency, checked BEFORE anything is diagnosed ──────────
  // A verdict drawn from counters that contradict each other is worse than no
  // verdict, because it points at an innocent stage with full confidence. The
  // payloader is capped at RTP_MTU bytes per packet by construction
  // (`rtph264pay mtu=1200`), so bytes ÷ packets above that is not a finding
  // about the video — it is proof that one of those two counters is wrong.
  const rtpBytes = bytes.rtp_out ?? 0;
  const rtpPkts = pipeline.rtp_sent ?? 0;
  const bytesPerPkt = rtpPkts > 0 ? rtpBytes / rtpPkts : 0;
  const counterConflict = bytesPerPkt > RTP_MTU * 1.05;
  if (counterConflict) {
    hints.push(
      `Pipeline-out counters disagree: ${Math.round(bytesPerPkt)} bytes per packet against a hard ${RTP_MTU}-byte payloader MTU. Either the packet count is low or the byte count is high — treat every percentage below the parser as unreliable until the udpsink row is available to break the tie.`,
    );
  }

  // Frames in vs frames out. Every hop can read ~100% while most of the stream
  // never arrives, because the hops count PACKETS and nothing on the path
  // reconciles them against frames.
  const framesIn = pipeline.pipeline_frames ?? null;
  const framesOut = fe.frames_decoded ?? null;
  const dur = report.actual_duration_sec || 0;

  // Silent jitterbuffer discard. Deliberately checked FIRST: it is invisible to
  // every other number here, so any later explanation would be wrong.
  const silentDrop =
    jb.available && jb.drop_on_latency === true && (jb.num_lost ?? 0) === 0 &&
    depayPct != null && depayPct < LOSS_THRESHOLD;

  if (silentDrop) {
    severity = 'bad';
    verdict =
      'rtspsrc is discarding RTP silently — drop-on-latency is ON and all loss counters read 0, ' +
      'which is what that discard looks like. Packets vanish before any counter on this path sees them.';
    hints.push('Set LIVE_RTSP_DROP_ON_LATENCY=false in the node .env and restart ingest-live.');
    hints.push(
      'Keyframes suffer worst: a frame needs EVERY packet, so a ~21-packet keyframe survives far less often than a 1-packet P-frame.',
    );
  } else {
    // Worst, not first. Chain order put a marginal 91% depayloader ahead of an
    // encoder passing 34% of its frames, and the verdict named the depayloader.
    const lossy = stages
      .filter(st => st.isLoss)
      .sort((a, b) => (a.survivalPct ?? 100) - (b.survivalPct ?? 100))[0];
    if (lossy) {
      severity = 'bad';
      verdict = `Loss localised to: ${lossy.label}` +
        (lossy.survivalPct != null ? ` (${lossy.survivalPct}% survives)` : '');
      if (lossy.key === 'depay' || lossy.key === 'parse') {
        hints.push('Bytes are lost inside the pipeline, not on the network — frames arrive incomplete and the depayloader discards them.');
        hints.push('Check the jitterbuffer block above and the camera stream profile.');
      }
      if (lossy.key === 'encode') {
        hints.push(
          `The encoder emitted ${encKeptPct}% of the frames the decoder handed it` +
          (transcodeDrops
            ? ` — the leaky queue in front of it discarded ${transcodeDrops}.`
            : '.'),
        );
        hints.push(
          'Do NOT assume the GPU. Measured on this fleet: GPU-decode-only (no encoder at all) and a full-CPU pipeline both hit the SAME frame rate as the NVENC path — the encoder was never the ceiling. Check what the camera actually sends first.',
        );
        hints.push(
          'Raising LIVE_TRANSCODE_QUEUE_BUFFERS absorbs bursts; it does not help if the encoder branch is persistently slower than the source.',
        );
      }
      if (lossy.key === 'sfu_recv') hints.push('Loss on the local UDP hop from ingest-live to the ULLFU — check socket buffers and CPU.');
      if (lossy.key === 'fe_recv') hints.push('Loss on the network between ULLFU and browser — check NACK count, RTT and TURN relay.');
    }
  }

  // Keyframe starvation is worth calling out even when byte survival looks fine:
  // it is what actually makes live view smear.
  const kfps = pipeline.avg_kfps;
  if (kfps != null && kfps > 0 && kfps < 0.3) {
    if (severity === 'ok') severity = 'warn';
    hints.push(`Only ${kfps} keyframes/s reaching the pipeline — the decoder spends most of its time with no valid reference frame.`);
  }

  if (jb.available && (jb.num_lost ?? 0) > 0) {
    hints.push(`Jitterbuffer counted ${jb.num_lost} genuinely lost packets (real gaps in the RTP sequence).`);
  }

  // A stage cannot deliver more than it was given. Above ~105% the two ends of
  // the run were not measured over the same window — the pipeline counters are
  // pushed by a sampling thread, so a stale read at either end shifts the
  // ledger. Say so rather than letting the reader trust the number.
  const inflated = stages.find(st => st.survivalPct != null && st.survivalPct > 105);
  if (inflated) {
    hints.push(
      `${inflated.label} reads ${inflated.survivalPct}% — above 100% means the pipeline and ULLFU counters were sampled over slightly different windows, not that packets were gained. Treat this run's absolute counts as approximate.`,
    );
  }

  if (framesIn != null && framesOut != null && dur > 0 && framesIn > 0 && framesOut < framesIn * 0.75) {
    if (severity === 'ok') severity = 'warn';
    hints.push(
      `Frame accounting: the pipeline parsed ${Math.round(framesIn / dur)} fps but the browser decoded ${Math.round(framesOut / dur * 10) / 10} fps. Packet hops cannot show this — they all read near 100% while most frames never became pictures. Look at the packets-per-frame on each side: ${(rtpPkts / framesIn).toFixed(1)} at the payloader vs ${((fe.packets_received ?? 0) / framesOut).toFixed(1)} at the browser. Those should match.`,
    );
  }

  if (transcoding && bytes.encode_kbps != null && bytes.rtsp_in_kbps != null &&
      bytes.encode_kbps > bytes.rtsp_in_kbps * 1.1) {
    if (severity === 'ok') severity = 'warn';
    hints.push(
      `The encoder is producing MORE bitrate than the camera sent (${bytes.rtsp_in_kbps} → ${bytes.encode_kbps} kbps). Re-encoding a low-bitrate source upward adds no detail and spends encoder capacity that is already the bottleneck — cap WEBRTC_TRANSCODE_BITRATE at or below the source rate.`,
    );
  }

  // Say so when the encoder simply cannot be judged, rather than reporting "no
  // loss found" and letting silence read as health.
  if (transcoding && !decodeFrames) {
    if (severity === 'ok') severity = 'warn';
    hints.push(
      'This pipeline transcodes but reports no decoder frame count, so the encoder cannot be checked at all — a frame drop there would be invisible here. The node is running an ingest-live build without the leaky-queue probes.',
    );
  }

  if (transcoding && !((bytes.encode ?? 0) > 0)) {
    hints.push(
      'This pipeline transcodes but reports no encoder byte counter, so stages after the encoder are measured against the camera and will understate survival. Expected when the node runs LIVE_PIPELINE_PROBES=light, which drops the per-packet probes on purpose — they cost more throughput than they are worth. Otherwise the node predates the encoder probe.',
    );
  }

  return { stages, verdict, severity, hints };
}
